/**
 * Enhanced Calendar Query to Include External Events
 * Fetches both tasks and external calendar events for display
 *
 * @ts-nocheck
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../config/supabase';
import { toDate } from '../../utils/dateTz';
import {
  computeEndTime,
  getGoalTextColor,
  getGoalBgColor,
  DEFAULT_DURATION_MINUTES,
} from '../calendar/calendarUtils';
import logger from '../../utils/logger';

/**
 * Query key factory for calendar queries.
 */
export const calendarKeys = {
  /** @param {string} userId */
  all: (userId) => ['calendar', userId],
  /** @param {string} userId @param {string} start @param {string} end */
  range: (userId, start, end) => ['calendar', userId, 'range', start, end],
};

/**
 * Fetch calendar tasks and external events for a given date range
 * @param {string} userId - User ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {Object} options - Query options
 * @returns {any} React Query result
 */
export const useCalendarEventsQuery = (
  /** @type {string} */ userId,
  /** @type {Date} */ startDate,
  /** @type {Date} */ endDate,
  /** @type {Object} */ options = {},
) => {
  return useQuery({
    queryKey: ['calendarEvents', userId, startDate?.toISOString(), endDate?.toISOString()],
    queryFn: () => fetchCalendarEvents(userId, startDate, endDate),
    enabled: !!userId && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Fetch both tasks and external events
 * @param {string} userId - User ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Promise<any[]>} Combined array of tasks and external events
 */
async function fetchCalendarEvents(
  /** @type {string} */ userId,
  /** @type {Date} */ startDate,
  /** @type {Date} */ endDate,
) {
  // Convert to ISO strings for Supabase
  const startIso = (toDate(startDate) || new Date()).toISOString();
  const endIso = (toDate(endDate) || new Date()).toISOString();

  // Fetch cached calendar colors from user profile
  /** @type {Record<string, string>} */
  let calendarColors = {};
  try {
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('calendar_colors')
      .eq('user_id', userId)
      .single();
    if (profile?.calendar_colors && typeof profile.calendar_colors === 'object') {
      calendarColors = profile.calendar_colors;
    }
  } catch {
    // Non-critical — fall back to default gray
  }

  // Fetch tasks with start_time that overlap the view range
  // Overlap logic: event.start < viewEnd AND (event.end > viewStart OR event.end IS NULL)
  const { data: tasks, error: tasksError } = await supabase
    .from('tasks')
    .select(
      `
      *,
      goals!fk_linked_goal (
        id,
        title,
        color_key
      ),
      plans!fk_plan (
        id,
        title
      )
    `,
    )
    .eq('user_id', userId)
    .not('start_time', 'is', null)
    .lt('start_time', endIso)
    .or(`end_time.gt.${startIso},end_time.is.null`)
    .is('deleted_at', null)
    .order('start_time', { ascending: true });

  if (tasksError) {
    logger.error('fetchCalendarEvents: tasks query failed', tasksError);
    throw tasksError;
  }

  // Fetch external events
  // Use simplified overlap logic: start < viewEnd AND end > viewStart
  const { data: externalEvents, error: externalError } = await supabase
    .from('external_events')
    .select('*')
    .eq('user_id', userId)
    .lt('start_time', endIso)
    .gt('end_time', startIso)
    .order('start_time', { ascending: true });

  if (externalError) {
    logger.error('fetchCalendarEvents: external_events query failed', externalError);
    throw externalError;
  }

  logger.info(
    `📅 fetchCalendarEvents: ${tasks.length} tasks, ${externalEvents.length} external events`,
  );

  // Combine and format events
  const combinedEvents = [
    ...tasks.map((task) => {
      const colorKey = task.goals?.color_key ?? null;
      const isCompleted = task.done || task.is_completed || task.completed || false;
      const isAllDay = !!(task.is_all_day && task.all_day_date);
      const endTime = isAllDay
        ? null
        : computeEndTime(task.start_time, task.end_time, task.estimated_duration_minutes);

      return {
        ...task,
        type: 'task',
        id: task.id,
        title: task.task || task.title || 'Untitled Task',
        start: isAllDay ? task.all_day_date : task.start_time,
        end: isAllDay ? undefined : endTime ? endTime.toISOString() : task.start_time,
        allDay: isAllDay,
        backgroundColor: getGoalBgColor(colorKey),
        borderColor: getGoalTextColor(colorKey),
        textColor: getGoalTextColor(colorKey),
        classNames: isCompleted ? ['fc-event-completed'] : [],
        editable: true,
        extendedProps: {
          taskId: task.id,
          isCompleted,
          goalId: task.linked_goal_id,
          goalTitle: task.goals?.title,
          colorKey,
          durationMinutes: task.estimated_duration_minutes || DEFAULT_DURATION_MINUTES,
          updatedAt: task.updated_at,
        },
      };
    }),
    ...externalEvents.map((event) => {
      const calColor = calendarColors[event.calendar_id] || null;
      const isAllDay = event.is_all_day || false;
      // FullCalendar requires date-only strings (YYYY-MM-DD) for all-day events.
      // ISO timestamps cause all-day events to render in the timed grid instead.
      const start = isAllDay ? event.start_time.slice(0, 10) : event.start_time;
      const end = isAllDay ? event.end_time.slice(0, 10) : event.end_time;
      return {
        ...event,
        type: 'external',
        id: `external-${event.id}`,
        title: event.title || 'Busy',
        start,
        end,
        allDay: isAllDay,
        backgroundColor: calColor ? `${calColor}33` : 'var(--chakra-colors-gray-200)',
        borderColor: calColor || 'var(--chakra-colors-gray-400)',
        textColor: calColor || 'var(--chakra-colors-gray-700)',
        editable: false,
        extendedProps: {
          isExternal: true,
          calendarId: event.calendar_id,
          location: event.location,
          description: event.description,
          isPrivacyMode: event.is_privacy_mode,
        },
      };
    }),
  ];

  return combinedEvents;
}

/**
 * Original tasks-only query for backward compatibility
 * @param {string} userId - User ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @param {Object} options - Query options
 * @returns {any} React Query result
 */
export const useCalendarTasksQuery = (
  /** @type {string} */ userId,
  /** @type {Date} */ startDate,
  /** @type {Date} */ endDate,
  /** @type {Object} */ options = {},
) => {
  return useQuery({
    queryKey: ['calendarTasks', userId, startDate?.toISOString(), endDate?.toISOString()],
    queryFn: () => fetchCalendarTasks(userId, startDate, endDate),
    enabled: !!userId && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Fetch calendar tasks only
 * @param {string} userId - User ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Promise<any[]>} Array of tasks
 */
async function fetchCalendarTasks(
  /** @type {string} */ userId,
  /** @type {Date} */ startDate,
  /** @type {Date} */ endDate,
) {
  const startIso = (toDate(startDate) || new Date()).toISOString();
  const endIso = (toDate(endDate) || new Date()).toISOString();

  const { data, error } = await supabase
    .from('tasks')
    .select(
      `
      *,
      goals!fk_linked_goal (
        id,
        title,
        color_key
      ),
      plans!fk_plan (
        id,
        title
      )
    `,
    )
    .eq('user_id', userId)
    .not('start_time', 'is', null)
    .lt('start_time', endIso)
    .or(`end_time.gt.${startIso},end_time.is.null`)
    .is('deleted_at', null)
    .order('start_time', { ascending: true });

  if (error) throw error;
  return data;
}
